import {
  Component, ElementRef, EventEmitter, Input, Output, ViewChild,
  OnChanges, OnDestroy, SimpleChanges
} from '@angular/core';
import { VaultCrackerRound } from '../../../interfaces/vault-cracker.interface';

interface Tick { v: number; x1: number; y1: number; x2: number; y2: number; }
interface Numeral { v: number; x: number; y: number; label: string; }
interface Gem { x: number; y: number; color: string; coin: boolean; delay: number; facet: number; }
interface Knurl { x1: number; y1: number; x2: number; y2: number; }
type Band = 'green' | 'amber' | 'grey';

/**
 * Vault Cracker play surface — a life-like brushed-metal SAFE DIAL sitting on
 * a bank-vault door, plus a glass loot hopper that tumbles gems on a crack.
 *
 * The student DRAGS the red pointer needle around the dial (PointerEvents +
 * atan2 around the hub) or taps a numbered tick; the needle SNAPS to the
 * nearest stop and a pill on the tip shows the CURRENTLY SELECTED number
 * (their input — never the answer). The shell's SET button calls `commit()`.
 * A correct combination swings the vault door open and dispenses `dispenseCount`
 * gems into a hex-stacked pile (countable, with a count-up readout). The
 * dispensed count is `answer` for every sub-skill EXCEPT findPercent — there
 * `answer` is a PERCENT, so the loot is the real gem PART (percent% of whole).
 *
 * Productive struggle (theme §3.2): a wrong SET is NEVER a hard fail and plays
 * no buzzer. AFTER a committed SET the static bezel glows warmer/colder and a
 * more/less direction arrow appears (proximity feedback is shown ONLY post-SET
 * — during a live drag only the selected-number pill shows). The dial stays
 * live to retry. Fraction-ring benchmark marks illuminate only once the shell
 * raises `hintVisible` (the struggle beat).
 *
 * Presentational only: it owns the needle drag + the crack juice and reports
 * via `solved` / `missed`. Scoring / turns / hints / the coach live in the
 * shell + service.
 */
@Component({
  selector: 'app-vault-cracker-stage',
  templateUrl: './vault-cracker-stage.component.html',
  styleUrls: ['./vault-cracker-stage.component.scss']
})
export class VaultCrackerStageComponent implements OnChanges, OnDestroy {
  @Input() round: VaultCrackerRound | null = null;
  @Input() roundKey = 0;
  @Input() interactive = true;
  @Input() currLang: any = 'en';
  /** Raised by the shell after the struggle beat to light the fraction ring. */
  @Input() hintVisible = false;

  /** Correct SET → emits the committed value. */
  @Output() solved = new EventEmitter<number>();
  /** Wrong SET → emits the committed value + the warmth band + fine-band flag. */
  @Output() missed = new EventEmitter<{ committed: number; band: Band; fine: boolean }>();

  @ViewChild('svg', { static: true }) svgRef!: ElementRef<SVGSVGElement>;

  readonly VIEW_W = 1000;
  readonly VIEW_H = 620;

  // Dial + door geometry (LTR baseline; mirrored via getters for RTL)
  readonly DIAL_CY = 300;
  readonly R_BEZEL = 192;
  readonly R_FACE = 158;
  readonly R_TICK_OUT = 160;
  readonly R_TICK_MAJ = 136;
  readonly R_TICK_MIN = 148;
  readonly R_NUM = 118;
  readonly R_FRAC = 100;
  readonly NEEDLE_LEN = 138;
  readonly DOOR_R = 238;

  readonly LOOT_CY = 300;

  private readonly GEM_COLORS = ['#17B26A', '#7A5AF8', '#15B8A6', '#FDB022'];

  selectedValue = 0;
  missCount = 0;
  solvedState = false;
  doorOpen = false;
  committedShown = false;
  committedBand: Band | null = null;
  arrowDir: 'more' | 'less' | null = null;
  gemCount = 0;

  private dragging = false;
  private timers: any[] = [];
  private countTimer: any = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['round'] || changes['roundKey']) this.reset();
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  private reset(): void {
    this.clearTimers();
    this.selectedValue = 0;
    this.missCount = 0;
    this.solvedState = false;
    this.doorOpen = false;
    this.committedShown = false;
    this.committedBand = null;
    this.arrowDir = null;
    this.gemCount = 0;
    this.dragging = false;
  }

  // ── Mirror-aware cluster centres (dial vs loot tank swap sides in RTL) ──
  get isAr(): boolean { return this.currLang === 'ar'; }
  get dialCX(): number { return this.isAr ? 660 : 340; }
  get lootCX(): number { return this.isAr ? 200 : 800; }

  // ── Round accessors ────────────────────────────────────────────────
  get dialMax(): number { return this.round?.dialMax ?? 100; }
  get tickStep(): number { return this.round?.tickStep ?? 5; }
  get answer(): number { return this.round?.answer ?? 0; }

  /** Coarser numeral step: ≤ 12 engraved numbers around the ring. */
  get majorStep(): number {
    const ts = this.tickStep;
    return ts * Math.max(1, Math.ceil((this.dialMax / ts) / 10));
  }

  private polar(v: number, r: number, cx = this.dialCX, cy = this.DIAL_CY): { x: number; y: number } {
    const rad = -Math.PI / 2 + (v / this.dialMax) * Math.PI * 2;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }

  /** Minor snap ticks at every `tickStep`. */
  get minorTicks(): Tick[] {
    const out: Tick[] = [];
    for (let v = 0; v <= this.dialMax; v += this.tickStep) {
      const a = this.polar(v, this.R_TICK_OUT);
      const b = this.polar(v, this.R_TICK_MIN);
      out.push({ v, x1: a.x, y1: a.y, x2: b.x, y2: b.y });
    }
    return out;
  }

  /** Major ticks + engraved numerals. Skip v===dialMax (it coincides with 0 at 12 o'clock). */
  get majorTicks(): Tick[] {
    const out: Tick[] = [];
    for (let v = 0; v < this.dialMax; v += this.majorStep) {
      const a = this.polar(v, this.R_TICK_OUT);
      const b = this.polar(v, this.R_TICK_MAJ);
      out.push({ v, x1: a.x, y1: a.y, x2: b.x, y2: b.y });
    }
    return out;
  }

  get numerals(): Numeral[] {
    const out: Numeral[] = [];
    for (let v = 0; v < this.dialMax; v += this.majorStep) {
      const p = this.polar(v, this.R_NUM);
      out.push({ v, x: p.x, y: p.y + 6, label: this.dig(v) });
    }
    return out;
  }

  /** Benchmark fraction-ring marks (¼ ½ ¾) — lit only on the hint beat. */
  get fractionMarks(): { x: number; y: number; label: string }[] {
    const specs = [
      { f: 0.25, label: '¼' },
      { f: 0.5, label: '½' },
      { f: 0.75, label: '¾' }
    ];
    return specs.map(s => {
      const p = this.polar(this.dialMax * s.f, this.R_FRAC);
      return { x: p.x, y: p.y + 5, label: s.label };
    });
  }

  /** Needle rotation in degrees (needle drawn pointing up at 0). */
  get needleAngle(): number { return (this.selectedValue / this.dialMax) * 360; }
  get needleTransform(): string { return `rotate(${this.needleAngle.toFixed(2)} ${this.dialCX} ${this.DIAL_CY})`; }

  /** Selected-number pill position — near the tip, kept upright. */
  get pill(): { x: number; y: number } { return this.polar(this.selectedValue, this.R_FACE + 26); }
  get selectedLabel(): string { return this.dig(this.selectedValue); }

  /** Knurled bezel dashes (deterministic). */
  get knurls(): Knurl[] {
    const out: Knurl[] = [];
    const n = 72;
    for (let i = 0; i < n; i++) {
      const rad = (i / n) * Math.PI * 2;
      out.push({
        x1: this.dialCX + (this.R_BEZEL - 14) * Math.cos(rad),
        y1: this.DIAL_CY + (this.R_BEZEL - 14) * Math.sin(rad),
        x2: this.dialCX + this.R_BEZEL * Math.cos(rad),
        y2: this.DIAL_CY + this.R_BEZEL * Math.sin(rad)
      });
    }
    return out;
  }

  /** Vault-door rim rivets (deterministic ring). */
  get rivets(): { x: number; y: number }[] {
    const out: { x: number; y: number }[] = [];
    const n = 20;
    for (let i = 0; i < n; i++) {
      const rad = (i / n) * Math.PI * 2;
      out.push({
        x: this.dialCX + (this.DOOR_R - 16) * Math.cos(rad),
        y: this.DIAL_CY + (this.DOOR_R - 16) * Math.sin(rad)
      });
    }
    return out;
  }

  /** Six locking bolts around the door (retract on a crack). */
  get bolts(): { x: number; y: number; a: number }[] {
    const out: { x: number; y: number; a: number }[] = [];
    const n = 6;
    for (let i = 0; i < n; i++) {
      const deg = (i / n) * 360;
      const rad = (deg * Math.PI) / 180;
      out.push({
        x: this.dialCX + (this.DOOR_R - 4) * Math.cos(rad),
        y: this.DIAL_CY + (this.DOOR_R - 4) * Math.sin(rad),
        a: deg
      });
    }
    return out;
  }

  /** Direction-arrow arc outside the bezel (turn up=more / down=less). */
  get arrowArc(): string {
    const rA = this.R_BEZEL + 18;
    // small arc near the top of the dial
    const p0 = this.polar(this.dialMax * 0.86, rA);
    const p1 = this.polar(this.dialMax * 0.14, rA);
    return `M ${p0.x.toFixed(1)} ${p0.y.toFixed(1)} A ${rA} ${rA} 0 0 1 ${p1.x.toFixed(1)} ${p1.y.toFixed(1)}`;
  }
  get arrowLabel(): string {
    if (this.arrowDir === 'more') return this.isAr ? 'أكثر ↑' : 'MORE ↑';
    return this.isAr ? 'أقل ↓' : 'LESS ↓';
  }
  get arrowLabelPos(): { x: number; y: number } {
    return { x: this.dialCX, y: this.DIAL_CY - this.R_BEZEL - 34 };
  }

  /** Centre label swaps per sub-skill. */
  get centerLabel(): { top: string; bottom: string } {
    const sub = this.round?.subSkill;
    if (sub === 'findWhole') return { top: this.isAr ? 'حجم' : 'VAULT', bottom: this.isAr ? 'الخزنة' : 'SIZE' };
    if (sub === 'findPercent') return { top: this.isAr ? 'النسبة' : 'PERCENT', bottom: '%' };
    return { top: this.isAr ? 'الجواهر' : 'GEMS', bottom: this.isAr ? '' : 'OUT' };
  }

  // ── Loot hopper + gem pile ─────────────────────────────────────────
  /**
   * The COUNTABLE gem quantity that spills out on a crack. This must always be
   * a real number of gems — so for findPercent (where `answer` is a PERCENT,
   * not a gem count) we dispense the PART (percent% of the whole) instead. The
   * value the student must SET is unaffected (still `answer`).
   */
  get dispenseCount(): number {
    const r = this.round;
    if (!r) return 0;
    if (r.subSkill === 'findPercent') return Math.round((r.answer / 100) * r.whole);
    return r.answer;
  }

  /** 2–3 gems already resting in the tray BEFORE the crack (deterministic). */
  get restingGems(): { x: number; y: number; color: string; coin: boolean }[] {
    const cx = this.lootCX;
    return [
      { x: cx - 34, y: 506, color: '#7A5AF8', coin: false },
      { x: cx,      y: 512, color: '#FDB022', coin: true  },
      { x: cx + 34, y: 506, color: '#15B8A6', coin: false }
    ];
  }

  get gemPile(): Gem[] {
    const per = 6;
    const gap = 26;
    const baseY = 486;
    return Array.from({ length: this.dispenseCount }, (_, i) => {
      const row = Math.floor(i / per);
      const col = i % per;
      const rowOffset = (row % 2) * (gap / 2);
      const x = this.lootCX - (per * gap) / 2 + col * gap + rowOffset + gap / 2;
      const y = baseY - row * (gap * 0.82);
      const color = this.GEM_COLORS[i % this.GEM_COLORS.length];
      return { x, y, color, coin: color === '#FDB022', delay: Math.min(i, 26) * 70, facet: i % 3 };
    });
  }

  // ── Interaction ────────────────────────────────────────────────────
  get canCommit(): boolean { return this.interactive && !this.solvedState; }

  onDialDown(evt: PointerEvent): void {
    if (!this.interactive || this.solvedState) return;
    evt.preventDefault();
    evt.stopPropagation();
    this.dragging = true;
    this.committedShown = false; // live drag hides warmth/arrow (only pill shows)
    this.updateFromEvent(evt);
    try { this.svgRef.nativeElement.setPointerCapture(evt.pointerId); } catch { /* older TVs */ }
  }

  onPointerMove(evt: PointerEvent): void {
    if (!this.dragging) return;
    this.updateFromEvent(evt);
  }

  onPointerUp(evt: PointerEvent): void {
    if (!this.dragging) return;
    this.dragging = false;
    try { this.svgRef.nativeElement.releasePointerCapture(evt.pointerId); } catch { /* ignore */ }
  }

  /** Tap a tick to select it directly (touch-screen friendly). */
  onTickTap(v: number): void {
    if (!this.interactive || this.solvedState) return;
    this.committedShown = false;
    this.selectedValue = v;
  }

  private updateFromEvent(evt: PointerEvent): void {
    const p = this.toSvg(evt);
    let deg = (Math.atan2(p.y - this.DIAL_CY, p.x - this.dialCX) * 180) / Math.PI;
    let frac = (((deg + 90) % 360) + 360) % 360 / 360;
    let v = frac * this.dialMax;
    v = Math.round(v / this.tickStep) * this.tickStep;
    if (v > this.dialMax) v = this.dialMax;
    if (v < 0) v = 0;
    this.selectedValue = v;
  }

  // ── Commit (called by the shell's SET button) ──────────────────────
  commit(): void {
    if (!this.canCommit || !this.round) return;
    this.committedShown = false;
    const v = this.selectedValue;
    if (v === this.round.answer) {
      this.solvedState = true;
      this.doorOpen = true;
      this.arrowDir = null;
      this.committedBand = null;
      this.startCount();
      this.solved.emit(v);
    } else {
      this.missCount++;
      const band = this.bandFor(v);
      const fine = band === 'amber' || band === 'green';
      this.arrowDir = v < this.round.answer ? 'more' : 'less';
      this.committedBand = band;
      this.committedShown = true;
      this.missed.emit({ committed: v, band, fine });
    }
  }

  private bandFor(v: number): Band {
    const W = this.round?.subSkill === 'findPercent' ? 100 : (this.round?.whole ?? 100);
    const diff = Math.abs(v - this.answer);
    if (diff === this.tickStep && this.missCount >= 2) return 'green';
    if (diff <= 0.15 * W) return 'amber';
    return 'grey';
  }

  private startCount(): void {
    this.gemCount = 0;
    const total = this.dispenseCount;
    if (total <= 0) return;
    const dur = Math.min(1600, total * 90);
    const stepMs = Math.max(30, dur / total);
    this.countTimer = setInterval(() => {
      if (this.gemCount >= total) { clearInterval(this.countTimer); this.countTimer = null; return; }
      this.gemCount++;
    }, stepMs);
    this.timers.push(this.countTimer);
  }

  // ── Helpers ────────────────────────────────────────────────────────
  /** Render a number in Arabic-Indic digits under `ar`. */
  dig(n: number): string {
    const s = String(n);
    if (!this.isAr) return s;
    const map = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    return s.replace(/\d/g, d => map[+d]);
  }

  private toSvg(evt: PointerEvent): { x: number; y: number } {
    const svg = this.svgRef.nativeElement;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const m = pt.matrixTransform(ctm.inverse());
    return { x: m.x, y: m.y };
  }

  private clearTimers(): void {
    this.timers.forEach(t => { clearTimeout(t); clearInterval(t); });
    this.timers = [];
    if (this.countTimer) { clearInterval(this.countTimer); this.countTimer = null; }
  }
}
